/**
 * DrillPanel — the beat-2 drill surface for ONE cell: header (metric · site ·
 * period, close), breadcrumb trail, the §5 sum_check banner, and the row table.
 * All four §5/§8 outcomes render honestly: a reconciling list, a LOUD
 * mismatch, a typed refusal (no_detail_grain / non_additive_group — 200-status
 * capability states, not errors), and a 422 rejection message. Kit renders
 * from typed props only (CD-7); fetching lives in useDrill, state in useScope.
 */

import React from 'react'
import { AlertCircle, X } from 'lucide-react'
import InfoBanner from '../InfoBanner'
import type { ApiError } from '../../hooks/apiFetch'
import { isRefusal } from '../../hooks/useDrill'
import type {
  DrillRefusal,
  DrillResult,
  PathSegment,
  RegistryMetric,
  Site,
} from '../../types/metrics'
import { levelLabel, rowDescent } from './drillLevels'
import DrillBreadcrumbs from './DrillBreadcrumbs'
import DrillTable from './DrillTable'
import SumCheckBanner from './SumCheckBanner'
import TrustChip from './TrustChip'

interface DrillPanelProps {
  metricId: string
  metric: RegistryMetric | null // null = id not in the catalogue (bad deep link)
  site: Site
  period: string
  path: PathSegment[]
  result: DrillResult | undefined
  error: ApiError | null
  loading: boolean
  onNavigate: (path: PathSegment[]) => void
  onClose: () => void
}

const GROUP_RULE_WORDS: Record<string, string> = {
  weighted_avg: 'weighted average',
  simple_avg: 'simple average of site percentages',
  derived: 'derived value',
}

/** §5 refusal states, rendered honestly — never faked, never blank (§8). */
function RefusalState({
  refusal,
  metric,
}: {
  refusal: DrillRefusal
  metric: RegistryMetric | null
}): React.JSX.Element {
  const title = metric?.title ?? refusal.registry_ref
  const headline =
    refusal.refused === 'non_additive_group'
      ? `Group ${title} is a ${GROUP_RULE_WORDS[metric?.group_rule ?? ''] ?? 'non-additive rule'} — no row list sums to it`
      : `no detail grain — ${refusal.reason}`
  return (
    <div data-testid="drill-refusal" className="space-y-1 rounded-xl border border-line bg-panel2 px-3 py-2.5">
      <p className="text-sm font-medium text-ink">{headline}</p>
      {refusal.refused === 'non_additive_group' && (
        <p className="text-xs text-mut">{refusal.reason}</p>
      )}
      <TrustChip tone="neutral" label={refusal.registry_ref} title="registry reference" />
    </div>
  )
}

function PanelBody(props: DrillPanelProps): React.JSX.Element {
  const { metric, site, period, path, result, error, loading, onNavigate } = props
  if (error) {
    return (
      <InfoBanner variant="error" icon={AlertCircle} title="Drill rejected" message={error.message} />
    )
  }
  if (loading || !result) {
    return (
      <div role="status" className="animate-pulse rounded-xl border border-line px-4 py-6 text-center text-xs text-mut2">
        Loading drill…
      </div>
    )
  }
  if (isRefusal(result)) return <RefusalState refusal={result} metric={metric} />
  const level = result.level
  return (
    <div className="space-y-2">
      <DrillBreadcrumbs
        path={path}
        levelLabel={levelLabel(metric, site, level)}
        onNavigate={onNavigate}
      />
      <SumCheckBanner sumCheck={result.sum_check} />
      <DrillTable
        rows={result.rows}
        exportName={`drill_${props.metricId}_${period}_${level}`}
        descentFor={(row) => rowDescent(metric, site, level, row)}
        onDescend={(segment) => onNavigate([...path, segment])}
      />
    </div>
  )
}

export default function DrillPanel(props: DrillPanelProps): React.JSX.Element {
  const { metricId, metric, site, period, onClose } = props
  return (
    <section
      data-testid="drill-panel"
      aria-label={`Drill: ${metric?.title ?? metricId}`}
      className="cx-fade space-y-2 rounded-xl border border-line bg-panel p-4"
    >
      <header className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-ink">
          Drill — {metric?.title ?? metricId}
          <span className="ml-2 font-normal text-mut">
            {site} · {period}
          </span>
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close drill"
          className="rounded p-1 text-mut hover:bg-panel2 hover:text-ink"
        >
          <X size={14} />
        </button>
      </header>
      <PanelBody {...props} />
    </section>
  )
}
